/* eslint-disable @typescript-eslint/no-empty-function */
import React, { useCallback, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import "./MyClasses.css";
import { Layout, Row, Alert, Typography, Card } from "antd";
import { useTranslation, Trans } from "react-i18next";
import useInfiniteQuery from "hooks/useInfiniteQuery";
import { useNavigate } from "react-router-dom";

// custom components
import MyProgress from "common/my_progress";
import ClassList from "common/class_card/ClassList";
import ClassBanner from "common/class_banner";
import Button from "common/button";
import AddToCalendarModal from "common/modal/add_to_calendar_modal";

// graphql
import GET_MY_CLASSES from "graphql/queries/GetMyClasses";
import GET_PAST_CLASSES from "graphql/queries/GetPastClasses";
import GET_CURRENT_USER_INFO from "graphql/queries/GetCurrentUserInfo";

// hooks
import useModal from "hooks/useModal";

import help from "assets/help.png";
import { JoinCourseButton } from "common/class_banner/styles";
import useBannerBackground from "hooks/useBannerBackground";
import ClassesContext from "./MyClassesContext";

const { Content } = Layout;

const MAX_BOOKED_CLASSES_SIZE = window.matchMedia("(min-width: 768px)").matches
  ? 12
  : 4;
const MAX_PAST_CLASSES_SIZE = window.matchMedia("(min-width: 768px)").matches
  ? 12
  : 4;

function MyClasses() {
  const { t } = useTranslation("my_classes");
  const navigate = useNavigate();
  const [
    visibleAddToCalendarModal,
    showAddToCalendarModal,
    closeAddToCalendarModal,
  ] = useModal();

  const [showCourseWidth, setShowCourseWidth] = useState(0);
  const setWidthToClassCard = useCallback((width: number) => {
    setShowCourseWidth(width);
  }, []);
  const classesCtx = useMemo(
    () => ({
      setWidthToClassCard,
    }),
    [setWidthToClassCard]
  );
  const { loading, data, error } = useQuery(GET_MY_CLASSES, {
    fetchPolicy: "cache-and-network",
    variables: {
      first: MAX_BOOKED_CLASSES_SIZE,
    },
  });

  const { data: currentUserInfo } = useQuery(GET_CURRENT_USER_INFO);

  const { backgroundImage } = useBannerBackground();
  const {
    nodes: pastClasses = [],
    error: pastClassesError,
    pageInfo,
    ratedClassCount,
  } = useInfiniteQuery(
    "pastClasses",
    GET_PAST_CLASSES,
    {
      variables: {
        first: MAX_PAST_CLASSES_SIZE,
      },
    },
    true
  );
  // TODO:
  // Add error screen if there's an error
  if (error) {
    if (error.networkError) {
      return (
        <Alert
          type="error"
          style={{ marginTop: "64px" }}
          message={t("Network Error. Please try again later.")}
        />
      );
    }

    return (
      <Card style={{ marginTop: 64, textAlign: "left" }}>
        <p>{t("Some kind of ERROR, but don't worry, you can fix this:")}</p>
        <ol>
          <li>
            <Trans t={t}>
              <strong>Refresh</strong> your browser
            </Trans>
          </li>
          <li>
            <Trans t={t}>
              Log out of your Flexi Account (top right profile pic) and{" "}
              <strong>log in again</strong>
            </Trans>
          </li>
          <li>
            <Trans t={t}>
              Close and <strong>re-start</strong> your browser
            </Trans>
          </li>
          <li>
            <Trans t={t}>
              Try <strong>another</strong> browser to login
            </Trans>
          </li>
        </ol>
        <p>
          {t(
            "One of those solves the error in 99% of all cases. In the unlikely case they did not, tell us what you were trying to do and send us a screenshot to"
          )}{" "}
          <a href="mailto:support@flexiclasses.com">support@flexiclasses.com</a>
        </p>
      </Card>
    );
  }

  if (pastClassesError) {
    return (
      <Alert
        type="error"
        message="Error loading classes. Please check your internet or let us know if this keeps happening."
      />
    );
  }

  if (loading) {
    return (
      <div style={{ marginTop: 64 }}>
        <Alert type="info" message={t("Loading...")} />
      </div>
    );
  }

  const {
    bookedClasses: { nodes: bookedClasses, pageInfo: bookedPageInfo },
  } = data;

  const webcalUrl = currentUserInfo?.me?.webcalUrl;

  const navigateToCourse = () => {
    navigate("/new-book-course", {
      state: { course: bookedClasses[0]?.course },
    });
  };
  const isShowSuggestedClasses = bookedClasses.length === 0;
  return (
    <ClassesContext.Provider value={classesCtx}>
      <Content className="content-container my-classes-container">
        <div
          className={isShowSuggestedClasses ? "section-container" : ""}
          style={isShowSuggestedClasses ? backgroundImage : {}}
        >
          <div className="booking section" style={backgroundImage}>
            <div className="header" style={{ flexWrap: "wrap" }}>
              <div className="header-wraper">
                <div className="section-title" style={{ color: "white" }}>
                  {t("MY NEXT CLASS")}
                </div>
                <a
                  href="https://flexiclasses.com/faq-topic/my-classes/"
                  target="_blank"
                >
                  <button
                    type="button"
                    style={{ all: "unset", cursor: "pointer" }}
                  >
                    <img src={help} alt="Help" style={{ width: "60px" }} />
                  </button>
                </a>
              </div>
              {bookedClasses[0] && bookedClasses[0].course && (
                <div className="join-course" style={{ width: showCourseWidth }}>
                  <JoinCourseButton type="primary" onClick={navigateToCourse}>
                    {t("SHOW COURSE #")}
                    {bookedClasses[0]?.course?.id}
                  </JoinCourseButton>
                </div>
              )}
            </div>
            <ClassBanner
              currentClass={bookedClasses[0]}
              setButtonRef={() => {}}
            />
          </div>
          {
            // MY FUTURE CLASSES SECTION
            // 4 on mobile, 12 on desktop
            bookedClasses.length > 0 ? (
              <div className="booked-classes section">
                <div className="section-header" style={{ flexWrap: "wrap" }}>
                  <span className="section-title">
                    {t("MY FUTURE CLASSES")}
                  </span>
                  <div>
                    {/* If there's more than 12 classes, display VIEW ALL button */}
                    {bookedPageInfo?.hasNextPage && (
                      <Button
                        name="viewFutureClasses"
                        type="primary"
                        onClick={() => {
                          navigate(`/view-all/my-future-classes`);
                        }}
                      >
                        {t("ALL FUTURE CLASSES")}
                      </Button>
                    )}
                    <Button
                      style={{ marginLeft: "1rem" }}
                      name="addClassesToCalendar"
                      type="primary"
                      onClick={() => {
                        showAddToCalendarModal();
                      }}
                    >
                      {t("ADD CLASSES TO CALENDAR")}
                    </Button>
                  </div>
                  {webcalUrl && (
                    <AddToCalendarModal
                      useAlternative
                      visible={visibleAddToCalendarModal}
                      onCloseModal={closeAddToCalendarModal}
                      webcalUrl={webcalUrl}
                    />
                  )}
                </div>
                <Row className="section-cards" gutter={24}>
                  <ClassList lessons={bookedClasses} />
                </Row>
              </div>
            ) : null
          }

          {
            // Only show suggested classes if there's no booked classes
            bookedClasses.length === 0 ? (
              <div className="suggested-classes section">
                <div className="section-header section-title">
                  {t(`{{ language }} CLASSES`, {
                    language:
                      currentUserInfo?.me.currentLanguage.englishName.toUpperCase() ||
                      "",
                  })}
                </div>
                <Row className="section-cards" gutter={24}>
                  <ClassList lessons={data.suggestedClasses} allowsBooking />
                </Row>
              </div>
            ) : null
          }
        </div>
        <MyProgress showViewDetailButton />

        <div className="past-classes section">
          <div className="section-header" style={{ flexWrap: "wrap" }}>
            <span className="section-title" style={{ letterSpacing: "-1px" }}>
              {t("MY PAST CLASSES")}
            </span>
            <div
              className="section-contents-wrapper"
              style={{ display: "flex", textAlign: "right" }}
            >
              <div style={{ flexDirection: "column" }}>
                <a
                  href="https://ltl-school.com/travel-to-asia"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Typography.Title
                    level={3}
                    style={{ marginBottom: 0, color: "#1070b6" }}
                  >
                    {t(`I Rated {{ ratedClassCount }} Class`, {
                      ratedClassCount,
                      count: ratedClassCount,
                    })}
                  </Typography.Title>
                </a>
                <Typography>
                  <a
                    href="https://flexiclasses.com/faq/how-can-i-redeem-a-free-week-in-china-when-i-rate-100-classes/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t(
                      `Rate 100 classes to get a free one week course at LTL Language school`
                    )}
                  </a>
                </Typography>
              </div>
              {pageInfo?.hasNextPage && (
                <Button
                  name="viewPastClasses"
                  type="primary"
                  onClick={() => {
                    navigate(`/view-all/my-past-classes`);
                  }}
                  style={{ marginLeft: 10, alignSelf: "center" }}
                >
                  {t("ALL PAST CLASSES")}
                </Button>
              )}
            </div>
          </div>
          <Row className="section-cards" gutter={24}>
            <ClassList
              lessons={pastClasses}
              showRibbon={{ completed: false, rated: true, past: false }}
            />
          </Row>
        </div>
      </Content>
    </ClassesContext.Provider>
  );
}

export default MyClasses;
